class MulticolumnVideoItem extends HTMLElement {
  constructor() {
    super();
    this.video = null;
    this.playButton = null;
    this.isPreviewPlaying = false;
    this.previewTimeout = null;
    this.previewDuration = parseInt(this.dataset.previewDuration) || 1500;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    this.video = this.querySelector('video');
    this.playButton = this.querySelector('.video-play-button');

    if (!this.video || !this.playButton) return;

    // Preview functionality
    this.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));

    // Play button click handler
    this.playButton.addEventListener('click', this.handlePlayButtonClick.bind(this));

    // Video event handlers
    this.video.addEventListener('pause', this.handleVideoPause.bind(this));
    this.video.addEventListener('play', this.handleVideoPlay.bind(this));
    this.video.addEventListener('ended', this.handleVideoEnded.bind(this));
  }

  handleMouseEnter() {
    if (!this.classList.contains('playing')) {
      this.isPreviewPlaying = true;
      this.video.currentTime = 0;
      this.video.play();
      this.classList.add('previewing');

      // Set timeout to pause video after preview duration
      this.previewTimeout = setTimeout(() => {
        if (this.isPreviewPlaying && !this.classList.contains('playing')) {
          this.video.pause();
          this.video.currentTime = 0;
          this.classList.remove('previewing');
          this.isPreviewPlaying = false;
        }
      }, this.previewDuration);
    }
  }

  handleMouseLeave() {
    if (this.isPreviewPlaying && !this.classList.contains('playing')) {
      clearTimeout(this.previewTimeout);
      this.video.pause();
      this.video.currentTime = 0;
      this.classList.remove('previewing');
      this.isPreviewPlaying = false;
    }
  }

  handlePlayButtonClick(e) {
    e.stopPropagation();
    this.clearPreviewState();
    
    if (this.video.paused) {
      this.video.play();
      this.classList.add('playing');
    } else {
      this.video.pause();
      this.classList.remove('playing');
    }
  }

  handleVideoPause() {
    if (!this.isPreviewPlaying) {
      this.classList.remove('playing');
    }
  }

  handleVideoPlay() {
    if (!this.isPreviewPlaying) {
      this.classList.add('playing');
    }
  }

  handleVideoEnded() {
    if (this.isPreviewPlaying) {
      this.clearPreviewState();
    }
  }

  clearPreviewState() {
    clearTimeout(this.previewTimeout);
    this.isPreviewPlaying = false;
    this.classList.remove('previewing');
  }

  cleanup() {
    if (this.video && this.playButton) {
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
      this.playButton.removeEventListener('click', this.handlePlayButtonClick);
      this.video.removeEventListener('pause', this.handleVideoPause);
      this.video.removeEventListener('play', this.handleVideoPlay);
      this.video.removeEventListener('ended', this.handleVideoEnded);
      this.clearPreviewState();
    }
  }
}

// Register the custom element
if (!customElements.get('multicolumn-video-item')) {
  customElements.define('multicolumn-video-item', MulticolumnVideoItem);
}

class MulticolumnSlider extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('.multicolumn-list');
    if (!this.slider) return;

    this.mobileSliderEnabled = this.dataset.mobileSlider !== 'false';
    if (!this.mobileSliderEnabled) return;

    this.slides = this.slider.querySelectorAll('.multicolumn-list__item');
    this.currentIndex = 0;
    this.isMobile = window.innerWidth <= 989;

    if (this.isMobile && this.slides.length > 1) {
      this.init();
    }

    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 989;
      if (this.isMobile && !wasMobile && this.slides.length > 1) {
        this.init();
      } else if (!this.isMobile && wasMobile) {
        this.destroy();
      }
    });
  }

  init() {
    this.createDots();
    this.slider.scrollLeft = 0;
    this.currentIndex = 0;
    this.updateSlide();
    this.addSwipeListeners();
  }

  destroy() {
    const dotsContainer = this.querySelector('.multicolumn-slider-dots');
    if (dotsContainer) dotsContainer.remove();
    this.slider.scrollLeft = 0;
  }

  createDots() {
    if (this.querySelector('.multicolumn-slider-dots')) return;
    const dotsContainer = document.createElement('div');
    dotsContainer.className = 'multicolumn-slider-dots';
    this.slides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.className = 'multicolumn-slider-dot';
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      if (index === 0) dot.classList.add('active');
      dot.addEventListener('click', () => this.goToSlide(index));
      dotsContainer.appendChild(dot);
    });
    this.appendChild(dotsContainer);
  }

  updateSlide() {
    if (!this.isMobile) return;
    const slideWidth = this.slides[0] ? this.slides[0].offsetWidth : window.innerWidth;
    this.slider.scrollTo({ left: this.currentIndex * slideWidth, behavior: 'smooth' });
    const dots = this.querySelectorAll('.multicolumn-slider-dot');
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === this.currentIndex);
    });
  }

  goToSlide(index) {
    this.currentIndex = index;
    this.updateSlide();
  }

  addSwipeListeners() {
    // Native CSS scroll-snap handles swipe gestures — only sync dots on scroll end
    let scrollTimeout;
    this.slider.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const slideWidth = this.slides[0] ? this.slides[0].offsetWidth : window.innerWidth;
        const newIndex = Math.round(this.slider.scrollLeft / slideWidth);
        if (newIndex !== this.currentIndex && newIndex >= 0 && newIndex < this.slides.length) {
          this.currentIndex = newIndex;
          const dots = this.querySelectorAll('.multicolumn-slider-dot');
          dots.forEach((dot, index) => {
            dot.classList.toggle('active', index === this.currentIndex);
          });
        }
      }, 80);
    }, { passive: true });
  }
}

if (!customElements.get('multicolumn-slider')) {
  customElements.define('multicolumn-slider', MulticolumnSlider);
}
